import React from 'react';
import MediaQuery from 'react-responsive';

import withMobileDialog from '@material-ui/core/withMobileDialog';

import { CreateDocumentTemplateDialog } from './CreateDocumentTemplateDialog';

import {
  StyledHeader,
  StyledMain,
  StyledHeaderContainer,
  StyledAddButton,
  StyledTableContainer,
} from 'helpers/StyledTableComponentsHelper';
import HeaderWithPeriod from 'components/shared/HeaderWithPeriod';
import TableFilters from 'components/ui/TableFilters';
import TabbedTable from 'components/ui/TabbedTable';
import MobileTable from 'components/ui/MobileTable';

import { toFriendlyDateString } from 'helpers/Formatter';
import { getTranslatedString } from 'helpers/i18n';

const documentStatuses = ['draft', 'ready_for_review', 'published'];

const documentStatusesLabels = {
  draft: getTranslatedString('landlordPortal.documentTemplate.draft'),
  ready_for_review: getTranslatedString('landlordPortal.documentTemplate.readyForReview'),
  published: getTranslatedString('landlordPortal.documentTemplate.published'),
};

const documentTypesLabels = {
  fixed_addendum: getTranslatedString('landlordPortal.documentTemplate.fixedAddendum'),
  conditional_addendum: getTranslatedString('landlordPortal.documentTemplate.conditionalAddendum'),
  lease: getTranslatedString('landlordPortal.documentTemplate.lease'),
};

class DocumentTemplatesTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filters: {
        search: '',
        properties: Array(this.props.properties.length).fill(false),
        documentStatuses: Array(documentStatuses.length).fill(false),
      },
      filtersActive: false,
      currentTab: 'all',
      sortKey: '',
      sortOrder: '',
      offset: 0,
    };
  }

  setRef = (ref) => (this.table = ref);

  handleTabChange = (tabName) => {
    this.setState(
      {
        currentTab: tabName,
        offset: 0,
      },
      () => this.fetchFilteredDocumentTemplates()
    );
    if (this.table) this.table.scrollToTop(0);
  };

  handleSort = (key, order) => {
    this.setState(
      {
        sortKey: key,
        sortOrder: order,
        offset: 0,
      },
      () => this.fetchFilteredDocumentTemplates()
    );
    if (this.table) this.table.scrollToTop(0);
  };

  handleClearFilters = (setFieldValue) => {
    this.setState(
      {
        filters: {
          search: '',
          properties: Array(this.props.properties.length).fill(false),
          documentStatuses: Array(documentStatuses.length).fill(false),
        },
        filtersActive: false,
        currentTab: 'lease',
        sortKey: '',
        sortOrder: '',
        offset: 0,
      },
      () => this.props.getAllDocumentTemplates()
    );

    if (this.table) this.table.scrollToTop(0);
    setFieldValue('search', '');
    setFieldValue('properties', Array(this.props.properties.length).fill(false));
    setFieldValue('documentStatuses', Array(documentStatuses.length).fill(false));
  };

  handleSubmitFilters = (values) => {
    const isFiltersUntouched =
      values.search === '' &&
      !values.properties.includes(true) &&
      !values.documentStatuses.includes(true);
    const newState = {
      filtersActive: !isFiltersUntouched,
      filters: {
        search: values.search,
        properties: values.properties,
        documentStatuses: values.documentStatuses,
      },
      drawerVisible: false,
      offset: 0,
    };

    this.setState(newState, () => this.fetchFilteredDocumentTemplates());
    if (this.table) this.table.scrollToTop(0);
  };

  fetchFilteredDocumentTemplates = () => {
    const { filters, filtersActive, currentTab, offset, sortKey, sortOrder } = this.state;

    let fetchPromise = null;

    let filterOptions = {
      sort_key: sortKey,
      sort_order: sortOrder,
    };

    const filterArray = (array, booleanArrayMask) => {
      return array.filter((_, i) => booleanArrayMask[i]);
    };

    if (filtersActive) {
      const propertiesToFilter = filterArray(this.props.properties, filters.properties).map(
        (data) => data.value
      );
      const statusesToFilter = filterArray(documentStatuses, filters.documentStatuses);

      filterOptions = {
        query: filters.search,
        property_ids: propertiesToFilter,
        document_statuses: statusesToFilter,
        sort_key: sortKey,
        sort_order: sortOrder,
      };

      fetchPromise = this.props.getFilteredDocumentTemplates('', offset, filterOptions);
    } else {
      fetchPromise = this.props.getFilteredDocumentTemplates(currentTab, offset, filterOptions);
    }

    fetchPromise.then(() => {
      this.setState({
        offset: this.props.parcels.length,
      });
    });
  };

  handleCreateNewDocumentTemplate = () => {
    this.props.fetchDocumentTemplatesAndStats();
  };

  columns = [
    {
      title: getTranslatedString('landlordPortal.documentTemplate.name'),
      dataKey: 'title',
      sortable: true,
      width: 150,
    },
    {
      title: getTranslatedString('landlordPortal.documentTemplate.property'),
      dataKey: 'property_uuids',
      sortable: true,
      render: ({ data }) => {
        let properties = this.props.properties.filter((i) => data.includes(i.unique_id));
        return properties && properties.length
          ? [...properties.map((p) => p.label)].join(', ')
          : '-';
      },
      width: 300,
    },
    {
      title: getTranslatedString('landlordPortal.documentTemplate.type'),
      dataKey: 'signable_doc_type',
      sortable: true,
      render: ({ data }) => {
        return documentTypesLabels[data];
      },
      width: 300,
    },
    {
      title: getTranslatedString('landlordPortal.documentTemplate.createdAt'),
      dataKey: 'created_at',
      sortable: true,
      render: ({ data }) => toFriendlyDateString(data),
      width: 150,
    },
    {
      title: getTranslatedString('landlordPortal.documentTemplate.lastUsed'),
      dataKey: 'last_used',
      sortable: true,
      render: ({ data }) => {
        return data ? toFriendlyDateString(data) : '-';
      },
      width: 150,
    },
    {
      title: getTranslatedString('landlordPortal.documentTemplate.status'),
      dataKey: 'status',
      sortable: true,
      render: ({ data }) => {
        return documentStatusesLabels[data];
      },
      width: 150,
    },
  ];

  render() {
    const { filters } = this.state;

    const filterOptions = [
      {
        useSearch: true,
        options: this.props.properties.map((data) => data.label),
        name: 'properties',
        title: 'Select properties to filter by',
      },
      {
        useSearch: false,
        options: documentStatuses,
        name: 'documentStatuses',
        title: 'Filter by document status',
      },
    ];

    let numOfTemplates = 0;
    let numOfArchivedTemplates = 0;

    if (this.props.templateCounts) {
      const totalTemplates =
        this.props.templateCounts.lease +
        this.props.templateCounts.fixed_addendum +
        this.props.templateCounts.conditional_addendum;
      numOfTemplates = totalTemplates || 0;
      numOfArchivedTemplates = this.props.templateCounts.archived || 0;
    }

    const tabs = [
      {
        title: getTranslatedString('landlordPortal.documentTemplate.allTemplatesTab'),
        count: numOfTemplates,
        onClick: () => this.handleTabChange('all'),
      },
      {
        title: getTranslatedString('landlordPortal.documentTemplate.archivedTemplateTab'),
        count: numOfArchivedTemplates,
        onClick: () => this.handleTabChange('archived'),
      },
    ];

    return (
      <StyledMain>
        <StyledHeaderContainer>
          <StyledHeader>
            <HeaderWithPeriod
              headerText={getTranslatedString('landlordPortal.documentTemplate.documentTemplate')}
              newStyle
            />
            <MediaQuery query="(min-device-width: 751px)">
              <CreateDocumentTemplateDialog
                handleCreateNewDocumentTemplate={this.handleCreateNewDocumentTemplate}
              >
                {({ toggle }) => (
                  <StyledAddButton onClick={toggle}>
                    {getTranslatedString('landlordPortal.documentTemplate.addTemplate')}
                  </StyledAddButton>
                )}
              </CreateDocumentTemplateDialog>
            </MediaQuery>
          </StyledHeader>
          <TableFilters
            filters={filters}
            handleSubmitFilters={this.handleSubmitFilters}
            handleClearFilters={this.handleClearFilters}
            filterOptions={filterOptions}
          />
        </StyledHeaderContainer>
        <StyledTableContainer>
          {this._renderDesktopView(tabs)}
          {this._renderMobileView()}
        </StyledTableContainer>
      </StyledMain>
    );
  }

  _renderDesktopView = (tabs) => (
    <MediaQuery query="(min-device-width: 750px)">
      <TabbedTable
        setRef={this.setRef}
        tabs={tabs}
        columns={this.columns}
        data={this.props.data}
        searchResultsCount={this.props.templatesSearchResultsCount}
        loadMoreData={this.fetchFilteredDocumentTemplates}
        isLoading={this.props.isLoading}
        filtersActive={this.state.filtersActive}
        onRowClick={this.props.fetchDocumentTemplateById}
        onColumnClick={(data) => this.handleSort(data.key, data.order)}
      />
    </MediaQuery>
  );

  _renderMobileView = () => (
    <MediaQuery query="(max-device-width: 750px)">
      <MobileTable
        groupRowsBy="title"
        data={this.props.data}
        columns={this.columns}
        stickyHeaderTop={this.state.filters.search ? '137px' : '107px'}
        fieldsAboveFold={[
          {
            title: getTranslatedString('landlordPortal.documentTemplate.name'),
            key: 'title',
          },
          {
            title: getTranslatedString('landlordPortal.documentTemplate.property'),
            key: 'message',
          },
        ]}
        fieldsBelowFold={[
          {
            title: getTranslatedString('landlordPortal.documentTemplate.createdAt'),
            key: 'created_at',
          },
          {
            title: getTranslatedString('landlordPortal.documentTemplate.lastUsed'),
            key: 'last_used',
          },
        ]}
        onRowClick={this.props.fetchDocumentTemplateById}
      />
    </MediaQuery>
  );
}

export default withMobileDialog()(DocumentTemplatesTable);
